import { useQuery } from '@apollo/client';
import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { Redirect, useHistory } from 'react-router-dom';

import {
  GetUserQueryGQLType,
  GetUserQueryVariablesGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { FundingRequestDetailViewContainer } from 'borrower/domains/fundingRequests/ui/detail/ViewContainer';
import { FundingRequestsProvider } from 'borrower/domains/fundingRequests/ui/manage/Context';
import {
  isFundingRequestManageStep,
  FundingRequestManageStep,
  FundingRequestManageViewContainer,
} from 'borrower/domains/fundingRequests/ui/manage/ViewContainer';
import { FundingRequestsViewTemplate } from 'borrower/domains/fundingRequests/ui/ViewTemplate';
import {
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useGetFundingRequestQuery } from 'packages/funding-requests/requests/requests.gql.generated';
import { GET_USER } from 'requests/queries.gql';

import 'source/borrower/borrower.scss';
/**
 * Converts hashFrangment to disired management step
 */
const getFundingRequestDesiredStep = (
  hashFragmentsString: string,
): FundingRequestManageStep | null => {
  if (_.isEmpty(hashFragmentsString)) {
    return null;
  }
  const fragmentString = _.trimStart(hashFragmentsString, '#');
  return isFundingRequestManageStep(fragmentString)
    ? (fragmentString as FundingRequestManageStep)
    : null;
};

interface FundingRequestRouteMatchParams {
  fundingRequestId: string; // TODO: change to use UUID type in the future, if possible
}

interface FundingRequestRouteProps
  extends RouteComponentProps<FundingRequestRouteMatchParams> {}

export const FundingRequestsRoute = (routeParams: FundingRequestRouteProps) => {
  const { fundingRequestId } = routeParams.match.params;
  const toast = useToast();
  const history = useHistory();
  const [isCurrent, setIsCurrent] = useState(false);
  const { data: userData, loading: userLoading } = useQuery<
    GetUserQueryGQLType,
    GetUserQueryVariablesGQLType
  >(GET_USER);

  const {
    data: fundingRequestData,
    loading: fundingRequestDataLoading,
    error,
  } = useGetFundingRequestQuery({
    variables: { fundingRequestId },
  });

  useEffect(() => {
    if (fundingRequestData) {
      const {
        fundingRequestWithFullDetails: { isCurrent: _isCurrent },
      } = fundingRequestData;
      setIsCurrent(!!_isCurrent);
    }
  }, [fundingRequestData]);

  if (
    !userData ||
    userLoading ||
    !fundingRequestData ||
    fundingRequestDataLoading
  ) {
    return (
      <Skeleton height="600px" data-testid="draw-request-id-route-loading" />
    );
  }
  const { user } = userData;

  if (error || !user) {
    return <Redirect to="/funding-requests" />;
  }

  const desiredStep = getFundingRequestDesiredStep(routeParams.location.hash);

  let routeLevelError = false;
  return (
    <Box className="main">
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            errorToastConfig.title = `Failed to load draw requests page`;
            routeLevelError = true;
            toast(errorToastConfig);
            history.push('/funding-requests');
          }
        }}
      >
        {isCurrent ? (
          <FundingRequestsViewTemplate
            fundingRequestId={fundingRequestId}
            isCreditFacilitySelectDisabled
            selectedTab="manage"
          >
            <FundingRequestsProvider
              borrowerKey={user.organization.slug}
              fundingRequestId={fundingRequestId}
            >
              <FundingRequestManageViewContainer
                desiredStepDefault={desiredStep}
              />
            </FundingRequestsProvider>
          </FundingRequestsViewTemplate>
        ) : (
          <FundingRequestDetailViewContainer
            fundingRequestId={fundingRequestId}
            creditFacilityId={
              fundingRequestData.fundingRequestWithFullDetails.creditFacilityId
            }
          />
        )}
      </Sentry.ErrorBoundary>
    </Box>
  );
};
